// ═══════════════════════════════════════════════════════════════════
//  ANDON v5 — single-poll chat, stable WebRTC, no interval races
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_URL, num, today, addDays } from './Shared';

const ANDON_URL  = `${API_URL}/andon.php`;
const CHAT_URL   = `${API_URL}/chat_api.php`;
const POLL_MS    = 3000;   // ONE poll every 3s covers messages + presence + signals
const HB_MS      = 15000;  // heartbeat only needed if tab is idle (no polls firing)

const ICE = { iceServers:[
  { urls:'stun:stun.l.google.com:19302' },
  { urls:'stun:stun1.l.google.com:19302' },
  { urls:'stun:stun.cloudflare.com:3478' },
]};

const ROLES = [
  { id:'admin',    label:'Admin',           emoji:'🔑', color:'#14181f' },
  { id:'qa',       label:'QA Inspector',    emoji:'✅', color:'#00a65e' },
  { id:'manager',  label:'Quality Manager', emoji:'🛡️', color:'#0073ea' },
  { id:'auditor',  label:'Factory Auditor', emoji:'📋', color:'#a25ddc' },
  { id:'training', label:'Training',        emoji:'🎓', color:'#0da99b' },
  { id:'supplier', label:'Supplier QA',     emoji:'🏭', color:'#fdab3d' },
];
const RC = Object.fromEntries(ROLES.map(r=>[r.id,r.color]));
const RE = Object.fromEntries(ROLES.map(r=>[r.id,r.emoji]));

const QUICK = {
  admin:    ['Quality performance this month','Worst suppliers by defects','Open CAPs & exceptions'],
  qa:       ['My inspections due this week','POs blocked at a gate','My open follow-up points'],
  manager:  ['Exceptions waiting for approval','CAP closure rate by supplier','Team inspection load'],
  auditor:  ['Audits due for follow-up','Suppliers graded C','CAPs past target date'],
  training: ['Who is eligible for CQB?','Course completion rates','Certificates expiring soon'],
  supplier: ['My open POs and inspection dates','My last inspection results','My open CAP items'],
};

const post = async (url, body) => {
  try {
    const r = await fetch(url, { method:'POST',
      headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) });
    if (!r.ok) return { success:false, error:`HTTP ${r.status}` };
    return await r.json();
  } catch(e) { return { success:false, error:e.message }; }
};
const chatPost = (action, params={}) => post(CHAT_URL, { action, ...params });

function fmtTime(ts) {
  if (!ts) return '';
  let d;
  if (typeof ts === 'number') { d = new Date(ts); }
  else { const s = String(ts); d = new Date(s.includes('T')?s:s.replace(' ','T')+'Z'); }
  if (isNaN(d)) return '';
  const now = new Date(), diff = (now-d)/1000;
  if (diff < 60)   return 'just now';
  if (diff < 3600) return Math.floor(diff/60)+'m ago';
  if (d.toDateString()===now.toDateString()) return d.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'});
  return d.toLocaleDateString([],{day:'2-digit',month:'short'});
}

function MD({ text }) {
  const html = (text||'')
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/\*\*(.+?)\*\*/g,'<b>$1</b>').replace(/\*(.+?)\*/g,'<i>$1</i>')
    .replace(/`(.+?)`/g,'<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px">$1</code>')
    .replace(/\n/g,'<br/>');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function Dots() {
  return <span style={{display:'inline-flex',gap:3}}>
    {[0,1,2].map(i=><span key={i} style={{width:5,height:5,borderRadius:'50%',background:'#94a3b8',
      animation:`andonBounce 1.2s ${i*0.15}s infinite ease-in-out`}}/>)}
  </span>;
}

const Dot = ({on}) => <span style={{width:8,height:8,borderRadius:'50%',display:'inline-block',
  flexShrink:0,background:on?'#00c875':'#cbd5e1'}}/>;

function localAnswer(q, db, role, supplierName) {
  const t=(q||'').toLowerCase(), has=(...w)=>w.some(x=>t.includes(x));
  const insp=db.inspections||[], caps=db.caps||[], exc=db.exceptions||[];
  const pos=db.pos||[], sups=db.suppliers||[];
  const mine=supplierName?insp.filter(i=>i.supplier===supplierName):insp;
  const done=mine.filter(i=>['Passed','Failed'].includes(i.result));
  const rate=done.length?Math.round(done.filter(i=>i.result==='Passed').length/done.length*100):null;
  if (has('pass rate','first pass','quality performance','how are we'))
    return `**Quality performance**\n\n• Inspections done: **${done.length}**\n• First-pass rate: **${rate!==null?rate+'%':'no data'}**\n• Open exceptions: **${exc.filter(e=>e.status==='Pending Approval').length}**`;
  if (has('worst','defect','major defect')) {
    const bySup={}; insp.forEach(i=>{bySup[i.supplier]=(bySup[i.supplier]||0)+num(i.totals?.critical)+num(i.totals?.major);});
    const top=Object.entries(bySup).sort((a,b)=>b[1]-a[1]).slice(0,5);
    return top.length?`**Top suppliers by defects**\n\n${top.map(([s,v],i)=>`${i+1}. ${s} — **${v}** defects`).join('\n')}`:'No defect data yet.';
  }
  if (has('cap','corrective'))
    return `**CAPs**\n\n• Total: **${caps.length}**\n• Open: **${caps.filter(c=>c.status!=='Closed').length}**`;
  if (has('exception','blocked'))
    return `**Exceptions**\n\n• Raised: **${exc.length}**\n• Pending: **${exc.filter(e=>e.status==='Pending Approval').length}**`;
  if (has('help','what can'))
    return `**I can answer from your live Toqum data.** Try:\n\n${(QUICK[role]||QUICK.qa).map(x=>`• ${x}`).join('\n')}\n\nSwitch to **Team** tab to message or call anyone online.`;
  return null;
}

function CallOverlay({ state, local, remote, onHangup, onAccept }) {
  const remRef=useRef(), locRef=useRef(), audRef=useRef();
  useEffect(()=>{if(remRef.current&&remote)remRef.current.srcObject=remote;},[remote]);
  useEffect(()=>{if(locRef.current&&local)locRef.current.srcObject=local;},[local]);
  useEffect(()=>{if(audRef.current&&remote)audRef.current.srcObject=remote;},[remote]);
  if (!state) return null;
  const isVideo=state.kind==='video';
  return (
    <div style={{position:'fixed',inset:0,zIndex:10001,background:'rgba(15,23,42,0.96)',
      display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:18,fontWeight:900,marginBottom:4}}>{state.peerName}</div>
      <div style={{color:'#94a3b8',fontSize:12,marginBottom:20}}>
        {state.status==='ringing'?(state.incoming?'Incoming call…':'Calling…')
          :state.status==='connected'?`${isVideo?'Video':'Audio'} call connected`:state.status}
      </div>
      {isVideo&&(
        <div style={{position:'relative',width:'min(680px,90vw)',aspectRatio:'16/9',
          background:'#000',borderRadius:14,overflow:'hidden',marginBottom:20}}>
          <video ref={remRef} autoPlay playsInline style={{width:'100%',height:'100%',objectFit:'cover'}}/>
          <video ref={locRef} autoPlay playsInline muted
            style={{position:'absolute',bottom:10,right:10,width:140,borderRadius:8,border:'2px solid rgba(255,255,255,0.3)'}}/>
        </div>
      )}
      {!isVideo&&<>
        <div style={{width:88,height:88,borderRadius:'50%',background:'#0073ea',
          display:'flex',alignItems:'center',justifyContent:'center',fontSize:38,marginBottom:20}}>🎧</div>
        <audio ref={audRef} autoPlay/>
      </>}
      <div style={{display:'flex',gap:12}}>
        {state.incoming&&state.status==='ringing'&&(
          <button onClick={onAccept} style={{padding:'11px 28px',borderRadius:24,border:'none',
            background:'#00c875',color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14}}>✓ Accept</button>
        )}
        <button onClick={onHangup} style={{padding:'11px 28px',borderRadius:24,border:'none',
          background:'#e2445c',color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14}}>
          {state.status==='connected'?'End call':'✕ Cancel'}</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
export default function Andon({ db={}, currentUser, currentModule='' }) {
  const [open,setOpen]     = useState(false);
  const [tab,setTab]       = useState('ai');
  const [role,setRole]     = useState(null);
  const [userName,setUserName] = useState('');
  const [suppName,setSuppName] = useState('');
  const [fabUnread,setFabUnread] = useState(0);

  const [aiMsgs,setAiMsgs]   = useState([]);
  const [aiInput,setAiInput] = useState('');
  const [aiLoading,setAiLoading] = useState(false);

  const [room,setRoom]       = useState('general');
  const [dmRooms,setDmRooms] = useState([]);
  const [msgMap,setMsgMap]   = useState({});
  const [presence,setPresence] = useState([]);
  const [chatIn,setChatIn]   = useState('');
  const [sending,setSending] = useState(false);

  // Use refs for values read inside the single poll interval
  const roomRef    = useRef('general');
  const lastIdRef  = useRef({});   // { roomId: maxId }
  const userIdRef  = useRef(null);
  const userNameRef= useRef('');
  const userRoleRef= useRef('');

  const [callState,setCallState] = useState(null);
  const [localSt,setLocalSt]   = useState(null);
  const [remoteSt,setRemoteSt] = useState(null);
  const pcRef     = useRef(null);
  const pendCands = useRef([]);
  const callRef   = useRef(null);

  const aiEnd=useRef(), chatEnd=useRef();

  // Keep refs in sync
  useEffect(()=>{ roomRef.current=room; },[room]);
  useEffect(()=>{ callRef.current=callState; },[callState]);

  // ── Identity ────────────────────────────────────────────────────
  useEffect(()=>{
    if (!currentUser) return;
    setUserName(currentUser.name||'');
    const r=(currentUser.role||'').toLowerCase();
    setRole(r.includes('super')||r.includes('admin')?'admin'
           :r.includes('manager')?'manager':r.includes('qa')?'qa':'qa');
  },[currentUser]);

  const userId = useMemo(()=>{
    if (!role||!userName) return null;
    const h=userName.split('').reduce((a,c)=>a+c.charCodeAt(0),0);
    return `${role}_${userName.toLowerCase().replace(/\s+/g,'_')}_${Math.abs(h)}`;
  },[role,userName]);

  // Keep refs in sync with state
  useEffect(()=>{ userIdRef.current=userId; },[userId]);
  useEffect(()=>{ userNameRef.current=userName; },[userName]);
  useEffect(()=>{ userRoleRef.current=role||''; },[role]);

  const ro=ROLES.find(r=>r.id===role);
  const rc=ro?.color||'#0073ea';

  useEffect(()=>{if(open&&tab==='ai')   aiEnd.current?.scrollIntoView({behavior:'smooth'});},[aiMsgs,aiLoading,open,tab]);
  useEffect(()=>{if(open&&tab==='team') chatEnd.current?.scrollIntoView({behavior:'smooth'});},[msgMap,room,open,tab]);
  useEffect(()=>{if(open) setFabUnread(0);},[open]);

  // ── Greeting ────────────────────────────────────────────────────
  useEffect(()=>{
    if (!role) return;
    const w={admin:'Full visibility. What do you need?',qa:'Your inspections and follow-ups.',
      manager:'Approvals, CAPs and team performance.',auditor:'Audits and CAP verification.',
      training:'Courses, assessments and CQB.',supplier:`Your orders and inspections${suppName?`, ${suppName}`:''}.`};
    setAiMsgs([{role:'assistant',ts:Date.now(),content:`**Andon** — pull the cord any time.\n\n${w[role]||'How can I help?'}`}]);
  },[role,suppName]);

  // ── Ask AI ──────────────────────────────────────────────────────
  const ask = useCallback(async (text)=>{
    const t=(text||aiInput).trim();
    if (!t||aiLoading) return;
    setAiInput('');
    const next=[...aiMsgs,{role:'user',content:t,ts:Date.now()}];
    setAiMsgs(next); setAiLoading(true);
    const local=localAnswer(t,db,role,role==='supplier'?suppName:null);
    try {
      const res=await post(ANDON_URL,{role,user_name:userName,supplier_name:suppName,
        messages:next.map(m=>({role:m.role,content:m.content})),
        context_summary:`Module:${currentModule}·Suppliers:${(db.suppliers||[]).length}·POs:${(db.pos||[]).length}`});
      if (res.success&&res.reply) setAiMsgs(p=>[...p,{role:'assistant',content:res.reply,ts:Date.now()}]);
      else if (local)             setAiMsgs(p=>[...p,{role:'assistant',content:local,ts:Date.now()}]);
      else throw new Error(res.error||'no reply');
    } catch { setAiMsgs(p=>[...p,{role:'assistant',ts:Date.now(),
        content:local||'Could not reach Andon service. Try a quick question.'}]); }
    setAiLoading(false);
  },[aiInput,aiLoading,aiMsgs,db,role,suppName,userName,currentModule]);

  // ── Register once on login ──────────────────────────────────────
  useEffect(()=>{
    if (!userId) return;
    chatPost('register',{user_id:userId,user_name:userName,role});
    // Load DM rooms
    chatPost('rooms',{user_id:userId}).then(r=>{if(r.success)setDmRooms(r.rooms||[]);});
  },[userId]); // eslint-disable-line

  // ── Load initial messages when room changes ─────────────────────
  useEffect(()=>{
    if (!userId||msgMap[room]) return;
    chatPost('get_messages',{room_id:room,after_id:0}).then(res=>{
      if (!res.success||!res.messages?.length) return;
      const msgs=res.messages;
      lastIdRef.current={...lastIdRef.current,[room]:msgs[msgs.length-1].id};
      setMsgMap(p=>({...p,[room]:msgs}));
    });
  },[room,userId]); // eslint-disable-line

  // ── THE ONE POLL — messages + presence + signals + heartbeat ────
  useEffect(()=>{
    if (!userId) return;
    let active=true;

    const poll=async()=>{
      const uid=userIdRef.current;
      if (!uid||!active) return;
      const r=roomRef.current;
      const afterId=lastIdRef.current[r]||0;
      const res=await chatPost('poll',{
        user_id:uid, user_name:userNameRef.current, role:userRoleRef.current,
        room:r, after_id:afterId,
      });
      if (!active||!res.success) return;

      // Messages
      if (res.messages?.length) {
        const msgs=res.messages;
        const maxId=msgs[msgs.length-1].id;
        lastIdRef.current={...lastIdRef.current,[r]:maxId};
        setMsgMap(p=>{
          const existing=p[r]||[];
          const existIds=new Set(existing.map(m=>m.id));
          const fresh=msgs.filter(m=>!existIds.has(m.id));
          return fresh.length?{...p,[r]:[...existing,...fresh]}:p;
        });
        setFabUnread(u=>open?u:u+msgs.length);
      }

      // Presence
      if (res.users) setPresence(res.users);

      // Signals (WebRTC)
      if (res.signals?.length) {
        for (const s of res.signals) {
          const payload=s.payload;
          if (s.type==='offer') {
            setCallState({peerId:s.from,peerName:s.from_name||'Incoming',
              kind:s.kind||'audio',status:'ringing',incoming:true,offer:payload});
          } else if (s.type==='answer'&&pcRef.current) {
            try { await pcRef.current.setRemoteDescription(new RTCSessionDescription(payload));
              setCallState(c=>c?{...c,status:'connected'}:c); } catch {}
          } else if (s.type==='candidate'&&payload) {
            try {
              if (pcRef.current?.remoteDescription) await pcRef.current.addIceCandidate(new RTCIceCandidate(payload));
              else pendCands.current.push(payload);
            } catch {}
          } else if (s.type==='bye') {
            hangup();
          }
        }
      }
    };

    poll(); // immediate first poll
    const iv=setInterval(poll,POLL_MS);
    return ()=>{ active=false; clearInterval(iv); };
  },[userId,open]); // eslint-disable-line

  // ── Send message ─────────────────────────────────────────────────
  const sendChat=useCallback(async()=>{
    const t=chatIn.trim();
    if (!t||sending||!userId) return;
    setSending(true); setChatIn('');
    const res=await chatPost('send',{room_id:room,user_id:userId,user_name:userName,role,body:t});
    if (res.success&&res.message) {
      const msg=res.message;
      setMsgMap(p=>{
        const existing=p[room]||[];
        if (existing.some(m=>m.id===msg.id)) return p;
        return {...p,[room]:[...existing,msg]};
      });
      lastIdRef.current={...lastIdRef.current,[room]:msg.id};
    }
    setSending(false);
  },[chatIn,sending,userId,room,userName,role]);

  // ── Open DM ──────────────────────────────────────────────────────
  const openDM=useCallback(async(u)=>{
    const res=await chatPost('dm',{user_id:userId,peer_id:u.user_id});
    if (res.success&&res.room_id) {
      setRoom(res.room_id);
      chatPost('rooms',{user_id:userId}).then(r=>{if(r.success)setDmRooms(r.rooms||[]);});
    }
  },[userId]);

  // ── WebRTC ───────────────────────────────────────────────────────
  const hangup=useCallback(()=>{
    pcRef.current?.close(); pcRef.current=null;
    localSt?.getTracks().forEach(t=>t.stop());
    setLocalSt(null); setRemoteSt(null);
    const cs=callRef.current;
    if (cs) chatPost('signal',{from:userId,to:cs.peerId,type:'bye',payload:''});
    setCallState(null);
  },[localSt,userId]);

  const startCall=useCallback(async(peer,kind)=>{
    try {
      const stream=await navigator.mediaDevices.getUserMedia({audio:true,video:kind==='video'});
      setLocalSt(stream);
      const pc=new RTCPeerConnection(ICE); pcRef.current=pc;
      stream.getTracks().forEach(t=>pc.addTrack(t,stream));
      pc.ontrack=e=>setRemoteSt(e.streams[0]);
      pc.onicecandidate=e=>{
        if (e.candidate) chatPost('signal',{from:userId,to:peer.user_id,type:'candidate',payload:e.candidate});
      };
      const offer=await pc.createOffer();
      await pc.setLocalDescription(offer);
      await chatPost('signal',{from:userId,to:peer.user_id,type:'offer',payload:offer,kind,from_name:userName});
      setCallState({peerId:peer.user_id,peerName:peer.user_name,kind,status:'ringing',incoming:false});
    } catch(e){alert('Could not start call: '+e.message);}
  },[userId,userName]);

  const acceptCall=useCallback(async()=>{
    const cs=callRef.current;
    if (!cs?.offer) return;
    try {
      const stream=await navigator.mediaDevices.getUserMedia({audio:true,video:cs.kind==='video'});
      setLocalSt(stream);
      const pc=new RTCPeerConnection(ICE); pcRef.current=pc;
      stream.getTracks().forEach(t=>pc.addTrack(t,stream));
      pc.ontrack=e=>setRemoteSt(e.streams[0]);
      pc.onicecandidate=e=>{
        if (e.candidate) chatPost('signal',{from:userId,to:cs.peerId,type:'candidate',payload:e.candidate});
      };
      await pc.setRemoteDescription(new RTCSessionDescription(cs.offer));
      for (const c of pendCands.current) { try{await pc.addIceCandidate(new RTCIceCandidate(c));}catch{} }
      pendCands.current=[];
      const ans=await pc.createAnswer();
      await pc.setLocalDescription(ans);
      await chatPost('signal',{from:userId,to:cs.peerId,type:'answer',payload:ans});
      setCallState(s=>s?{...s,status:'connected'}:s);
    } catch(e){alert('Could not accept: '+e.message); hangup();}
  },[userId,hangup]);

  // ── Derived ──────────────────────────────────────────────────────
  const onlineOthers=presence.filter(u=>u.user_id!==userId&&u.status==='online');
  const msgs=msgMap[room]||[];
  const CHANNELS=[
    {id:'general',name:'General'},{id:'quality',name:'Quality Alerts'},{id:'suppliers',name:'Suppliers'},
    ...dmRooms,
  ];

  // ══════════════════════ RENDER ══════════════════════════════════
  return (
    <>
      <style>{`
        @keyframes andonBounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}
        @keyframes andonSlideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes andonPulse{0%,100%{box-shadow:0 0 0 0 rgba(226,68,92,.45)}60%{box-shadow:0 0 0 12px rgba(226,68,92,0)}}
        .andon-fab:hover{transform:scale(1.09)!important}
        .andon-q:hover{background:#eef2f7!important}
        .andon-u:hover{background:#f4f7fb!important}
      `}</style>

      {/* FAB */}
      {!open&&(
        <button className="andon-fab" onClick={()=>setOpen(true)} title="Andon"
          style={{position:'fixed',bottom:26,right:26,zIndex:9999,width:60,height:60,
            borderRadius:'50%',border:'none',cursor:'pointer',display:'flex',
            alignItems:'center',justifyContent:'center',fontSize:25,transition:'transform .2s',
            background:'linear-gradient(135deg,#14181f,#e2445c)',
            boxShadow:'0 4px 20px rgba(226,68,92,.35)',animation:'andonPulse 3s ease-in-out infinite'}}>
          🪢
          {fabUnread>0&&<span style={{position:'absolute',top:2,right:2,minWidth:19,height:19,
            borderRadius:10,background:'#00c875',color:'#fff',fontSize:10.5,fontWeight:800,
            display:'flex',alignItems:'center',justifyContent:'center',padding:'0 5px'}}>{fabUnread}</span>}
        </button>
      )}

      {/* Panel */}
      {open&&(
        <div style={{position:'fixed',bottom:14,right:14,zIndex:9999,
          width:tab==='team'?700:430,maxWidth:'calc(100vw - 20px)',
          height:660,maxHeight:'calc(100vh - 28px)',background:'#fff',borderRadius:16,
          boxShadow:'0 28px 72px rgba(0,0,0,.22)',display:'flex',flexDirection:'column',
          overflow:'hidden',animation:'andonSlideUp .22s ease-out',transition:'width .25s ease'}}>

          {/* Header */}
          <div style={{background:'linear-gradient(135deg,#14181f,#243044)',color:'#fff',
            padding:'12px 16px',display:'flex',alignItems:'center',gap:11}}>
            <span style={{fontSize:21}}>🪢</span>
            <div style={{flex:1,lineHeight:1.25}}>
              <div style={{fontWeight:900,fontSize:14.5}}>ANDON</div>
              <div style={{fontSize:10,color:'#94a3b8'}}>{ro?`${RE[role]} ${ro.label}`:'Toqum assistant'}</div>
            </div>
            <select value={role||''} onChange={e=>setRole(e.target.value)}
              style={{background:'rgba(255,255,255,.1)',color:'#fff',border:'1px solid rgba(255,255,255,.2)',
                borderRadius:6,fontSize:10.5,padding:'4px 6px'}}>
              {ROLES.map(r=><option key={r.id} value={r.id} style={{color:'#000'}}>{r.label}</option>)}
            </select>
            <button onClick={()=>setOpen(false)}
              style={{background:'none',border:'none',color:'#94a3b8',fontSize:19,cursor:'pointer'}}>✕</button>
          </div>

          {/* Tabs */}
          <div style={{display:'flex',borderBottom:'1px solid #e6e9ef',background:'#fafbfd'}}>
            {[['ai','Ask Andon'],['team','Team']].map(([k,l])=>(
              <button key={k} onClick={()=>setTab(k)}
                style={{flex:1,padding:'10px 0',border:'none',cursor:'pointer',fontSize:12,fontWeight:800,
                  background:tab===k?'#fff':'transparent',color:tab===k?rc:'#8a94a6',
                  borderBottom:tab===k?`2px solid ${rc}`:'2px solid transparent'}}>
                {l}{k==='team'&&onlineOthers.length>0&&
                  <span style={{marginLeft:5,fontSize:10,color:'#00c875'}}>● {onlineOthers.length}</span>}
              </button>
            ))}
          </div>

          {/* ASK AI */}
          {tab==='ai'&&(
            <>
              <div style={{flex:1,overflowY:'auto',padding:14,background:'#f7f9fc'}}>
                {aiMsgs.map((m,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start',marginBottom:10}}>
                    <div style={{maxWidth:'86%',padding:'9px 13px',borderRadius:13,fontSize:12.5,
                      lineHeight:1.55,whiteSpace:'pre-wrap',
                      background:m.role==='user'?rc:'#fff',color:m.role==='user'?'#fff':'#243044',
                      border:m.role==='user'?'none':'1px solid #e6e9ef',
                      borderBottomRightRadius:m.role==='user'?4:13,borderBottomLeftRadius:m.role==='user'?13:4}}>
                      <MD text={m.content}/>
                      <div style={{fontSize:9,opacity:.55,marginTop:4}}>{fmtTime(m.ts)}</div>
                    </div>
                  </div>
                ))}
                {aiLoading&&<div style={{padding:'9px 13px',background:'#fff',borderRadius:13,
                  border:'1px solid #e6e9ef',display:'inline-block'}}><Dots/></div>}
                <div ref={aiEnd}/>
              </div>
              <div style={{padding:'8px 12px',borderTop:'1px solid #e6e9ef',background:'#fff'}}>
                <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:7}}>
                  {(QUICK[role]||QUICK.qa).map(q=>(
                    <button key={q} className="andon-q" onClick={()=>ask(q)}
                      style={{padding:'4px 9px',borderRadius:12,border:'1px solid #e6e9ef',
                        background:'#f7f9fc',fontSize:10.5,cursor:'pointer',color:'#4a5568'}}>{q}</button>
                  ))}
                </div>
                <div style={{display:'flex',gap:7}}>
                  <input value={aiInput} onChange={e=>setAiInput(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&ask()}
                    placeholder="Ask about inspections, suppliers, CAPs…"
                    style={{flex:1,padding:'9px 12px',border:'1px solid #cbd5e1',borderRadius:20,
                      fontSize:12.5,outline:'none'}}/>
                  <button onClick={()=>ask()} disabled={aiLoading}
                    style={{width:38,height:38,borderRadius:'50%',border:'none',background:rc,
                      color:'#fff',cursor:'pointer',fontSize:15}}>➤</button>
                </div>
              </div>
            </>
          )}

          {/* TEAM CHAT */}
          {tab==='team'&&(
            <div style={{flex:1,display:'flex',minHeight:0}}>
              {/* Sidebar */}
              <div style={{width:200,borderRight:'1px solid #e6e9ef',display:'flex',
                flexDirection:'column',background:'#fafbfd',flexShrink:0}}>
                <div style={{padding:'9px 12px',fontSize:10,fontWeight:800,color:'#8a94a6',
                  textTransform:'uppercase',letterSpacing:'.06em'}}>Channels</div>
                <div style={{overflowY:'auto',maxHeight:160}}>
                  {CHANNELS.map(c=>(
                    <div key={c.id} onClick={()=>setRoom(c.id)} className="andon-u"
                      style={{padding:'7px 12px',cursor:'pointer',fontSize:12,
                        background:room===c.id?'#eef4ff':'transparent',
                        color:room===c.id?rc:'#3d4450',fontWeight:room===c.id?700:500}}>
                      # {c.name}
                    </div>
                  ))}
                </div>
                <div style={{padding:'9px 12px',fontSize:10,fontWeight:800,color:'#8a94a6',
                  textTransform:'uppercase',letterSpacing:'.06em',borderTop:'1px solid #e6e9ef'}}>
                  Online — {onlineOthers.length}
                </div>
                <div style={{flex:1,overflowY:'auto'}}>
                  {onlineOthers.map(u=>(
                    <div key={u.user_id} className="andon-u"
                      style={{padding:'7px 10px',display:'flex',alignItems:'center',gap:6,fontSize:11.5}}>
                      <Dot on={true}/>
                      <span onClick={()=>openDM(u)} style={{flex:1,cursor:'pointer',overflow:'hidden',
                        textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {RE[u.role]||'👤'} {u.user_name}
                      </span>
                      <span onClick={()=>startCall(u,'audio')} title="Audio call"
                        style={{cursor:'pointer',fontSize:13}}>📞</span>
                      <span onClick={()=>startCall(u,'video')} title="Video call"
                        style={{cursor:'pointer',fontSize:13}}>📹</span>
                    </div>
                  ))}
                  {onlineOthers.length===0&&(
                    <div style={{padding:'10px 12px',fontSize:11,color:'#8a94a6'}}>Nobody else is online.</div>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>
                <div style={{padding:'6px 12px',borderBottom:'1px solid #e6e9ef',fontSize:12,
                  fontWeight:700,color:'#3d4450',background:'#fff'}}>
                  # {CHANNELS.find(c=>c.id===room)?.name||room}
                </div>
                <div style={{flex:1,overflowY:'auto',padding:12,background:'#f7f9fc'}}>
                  {msgs.map((m,i)=>{
                    const mine=m.user_id===userId;
                    return (
                      <div key={m.id||i} style={{display:'flex',
                        justifyContent:mine?'flex-end':'flex-start',marginBottom:8}}>
                        <div style={{maxWidth:'76%',padding:'8px 12px',borderRadius:12,fontSize:12.5,
                          background:mine?rc:'#fff',color:mine?'#fff':'#243044',
                          border:mine?'none':'1px solid #e6e9ef',lineHeight:1.45}}>
                          {!mine&&<div style={{fontSize:10,fontWeight:800,
                            color:RC[m.role]||'#8a94a6',marginBottom:2}}>
                            {RE[m.role]||'👤'} {m.user_name}</div>}
                          <span style={{whiteSpace:'pre-wrap'}}>{m.body}</span>
                          <div style={{fontSize:9,opacity:.5,marginTop:3}}>{fmtTime(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                  {msgs.length===0&&(
                    <div style={{textAlign:'center',color:'#8a94a6',fontSize:12,paddingTop:36}}>
                      No messages yet in this channel.
                    </div>
                  )}
                  <div ref={chatEnd}/>
                </div>
                <div style={{padding:'9px 12px',borderTop:'1px solid #e6e9ef',display:'flex',gap:7}}>
                  <input value={chatIn} onChange={e=>setChatIn(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&sendChat()}
                    placeholder={`Message #${CHANNELS.find(c=>c.id===room)?.name||room}`}
                    style={{flex:1,padding:'9px 12px',border:'1px solid #cbd5e1',borderRadius:20,
                      fontSize:12.5,outline:'none'}}/>
                  <button onClick={sendChat} disabled={sending}
                    style={{width:38,height:38,borderRadius:'50%',border:'none',
                      background:sending?'#cbd5e1':rc,color:'#fff',
                      cursor:sending?'default':'pointer',fontSize:15,transition:'background .15s'}}>➤</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <CallOverlay state={callState} local={localSt} remote={remoteSt}
        onHangup={hangup} onAccept={acceptCall}/>
    </>
  );
}
